import moment from 'moment'
import { multiSelect } from 'src/common/selectors'
import { obterDataValida } from 'src/utils/formatadoras/formatadoras'
import { resetarParaEstadoInicial } from 'src/utils/executoras/executoras'
import { API } from 'src/services'
import {
  montarPayloadDaConfigAtual,
  normalizarDetalheHistorico,
  normalizarHistorico,
  obterIdDoRelatorio
} from './utilitarios'
import {
  ACAO_CANCELAR,
  ACAO_HISTORICO,
  ORIGENS_DA_SELECAO_DA_ESCALA_TEMPORARIA,
  TIPOS_DE_FILTRO_DA_ESCALA_TEMPORARIA
} from './constantes'

const defaultState = () => ({
  ...multiSelect.mapStates(['empregados', 'locaisDeTrabalho', 'empregadores']),
  tipoFiltro: TIPOS_DE_FILTRO_DA_ESCALA_TEMPORARIA.EMPREGADOS,
  origemSelecao: ORIGENS_DA_SELECAO_DA_ESCALA_TEMPORARIA.LISTA,
  arquivoSelecao: null,
  dataInicial: null,
  dataFinal: null,
  escala: null,
  seletorEscala: {
    indice: null,
    selecionado: null
  },
  deslocamentoDaJornada: 0,
  faseInicialSelecionada: false,
  processamento: null,
  historico: {
    filtros: {
      dataInicial: null,
      dataFinal: null
    },
    itens: [],
    paginacao: null,
    detalhesPorId: {}
  },
  carregamento: {
    enviar: false,
    importar: false,
    listar: false,
    detalhar: false,
    excluir: false
  }
})

const __state = defaultState()

const getters = {
  configAtual (state) {
    return {
      empregados: state.empregados,
      locaisDeTrabalho: state.locaisDeTrabalho,
      empregadores: state.empregadores,
      tipoFiltro: state.tipoFiltro,
      origemSelecao: state.origemSelecao,
      arquivoSelecao: state.arquivoSelecao,
      dataInicial: state.dataInicial,
      dataFinal: state.dataFinal,
      escala: state.escala,
      deslocamentoDaJornada: state.deslocamentoDaJornada,
      faseInicialSelecionada: state.faseInicialSelecionada
    }
  }
}

const mutations = {
  ...multiSelect.mapMutations(['empregados', 'locaisDeTrabalho', 'empregadores']),
  resetState (state) {
    resetarParaEstadoInicial(state, defaultState())
  },
  dataInicial (state, date) {
    state.dataInicial = obterDataValida(date)
  },
  dataFinal (state, date) {
    state.dataFinal = obterDataValida(date)
  },
  seletorEscala (state, payload) {
    state.seletorEscala = {
      indice: payload?.index ?? null,
      selecionado: payload?.selected ?? null
    }
    state.escala = payload && payload.selected ? payload.selected : null
    state.deslocamentoDaJornada = 0
    state.faseInicialSelecionada = false
  },
  deslocamentoDaJornada (state, deslocamentoDaJornada) {
    state.deslocamentoDaJornada = deslocamentoDaJornada
    state.faseInicialSelecionada = true
  },
  tipoFiltro (state, tipoFiltro) {
    state.tipoFiltro = tipoFiltro
    state.empregados.selected = []
    state.locaisDeTrabalho.selected = []
    state.empregadores.selected = []
    state.arquivoSelecao = null
  },
  origemSelecao (state, origemSelecao) {
    state.origemSelecao = origemSelecao
    state.arquivoSelecao = null
  },
  arquivoSelecao (state, arquivoSelecao) {
    state.arquivoSelecao = arquivoSelecao
  },
  limparDadosDaPlanilha (state) {
    state.arquivoSelecao = null
  },
  processamento (state, processamento) {
    state.processamento = processamento
  },
  historico (state, historico) {
    state.historico = {
      ...state.historico,
      ...historico
    }
  },
  historicoFiltrosDataInicial (state, date) {
    state.historico.filtros.dataInicial = obterDataValida(date)
  },
  historicoFiltrosDataFinal (state, date) {
    state.historico.filtros.dataFinal = obterDataValida(date)
  },
  detalheHistoricoPorId (state, detalhe) {
    state.historico.detalhesPorId = {
      ...state.historico.detalhesPorId,
      [detalhe.id]: detalhe
    }
  },
  carregamento (state, { campo, valor }) {
    state.carregamento[campo] = Boolean(valor)
  }
}

const actions = {
  async enviarEscalaTemporaria ({ commit, getters }) {
    commit('carregamento', { campo: 'enviar', valor: true })

    try {
      const resposta = await API.escalaTemporariaCriacaoEmMassa.save(
        {},
        montarPayloadDaConfigAtual(getters.configAtual)
      )
      const idRelatorio = obterIdDoRelatorio(resposta)

      if (!idRelatorio) {
        throw new Error('Identificador do relatório da escala temporária não informado.')
      }

      commit('processamento', { relatorio: idRelatorio })
      return idRelatorio
    } finally {
      commit('carregamento', { campo: 'enviar', valor: false })
    }
  },
  async importarEscalasTemporarias ({ commit }, payload) {
    commit('carregamento', { campo: 'importar', valor: true })

    try {
      return await API.ImportadorEscalaTemporaria.save(payload)
    } finally {
      commit('carregamento', { campo: 'importar', valor: false })
    }
  },
  async buscarHistoricoEscalaTemporaria ({ state, commit }) {
    commit('carregamento', { campo: 'listar', valor: true })

    try {
      const { dataInicial, dataFinal } = state.historico.filtros
      const body = {
        dataInicial: moment(dataInicial).format('YYYY-MM-DDT00:00:00'),
        dataFinal: moment(dataFinal).format('YYYY-MM-DDT00:00:00'),
        // A tabela pagina do lado do cliente; busca-se um lote único do período filtrado.
        paginacao: { pagina: 1, por_pagina: 200 }
      }

      const resposta = await API.escalaTemporaria.save({ action: ACAO_HISTORICO }, body)
      commit('historico', normalizarHistorico(resposta))
    } finally {
      commit('carregamento', { campo: 'listar', valor: false })
    }
  },
  async buscarDetalhesHistoricoEscalaTemporaria ({ state, commit }, alteracaoDoHistorico) {
    const idDaAlteracao = alteracaoDoHistorico?.id

    if (!idDaAlteracao || state.historico.detalhesPorId[idDaAlteracao]) {
      return state.historico.detalhesPorId[idDaAlteracao]
    }

    commit('carregamento', { campo: 'detalhar', valor: true })

    try {
      // Sem endpoint de detalhe dedicado: o detalhe é o próprio item do histórico.
      const detalhe = normalizarDetalheHistorico(alteracaoDoHistorico)

      commit('detalheHistoricoPorId', detalhe)
      return detalhe
    } finally {
      commit('carregamento', { campo: 'detalhar', valor: false })
    }
  },
  async excluirEscalaTemporaria ({ commit }, id) {
    commit('carregamento', { campo: 'excluir', valor: true })

    try {
      const resposta = await API.escalaTemporaria.save({ id, action: ACAO_CANCELAR })
      return resposta
    } finally {
      commit('carregamento', { campo: 'excluir', valor: false })
    }
  }
}

export default {
  namespaced: true,
  state: __state,
  getters,
  mutations,
  actions
}
